import { useState, useEffect, useCallback } from 'preact/hooks'
import { apiClient, InitData, tenantSiteUrl, assetUrl, udLink } from '../../core'
import { t, intlLocale, formatMoney } from '../../core/i18n'
import { isRegistrationClosed } from './registration'
// Retreatens tidsrum i studiets tidszone, ikke den besøgendes browser (QA 27-10).
import { fmtDatoInterval, studietsTidszone } from '../../core/tidszone'
import type { RetreatData, RetreatTier } from './types'
import { RetreatBookingModal } from './booking-modal/RetreatBookingModal'
import { parseMarkdown } from './markdown'

// Data types live in ./types (a port of the tenant public contract) so the
// booking modal and this widget share one shape.
type RetreatDetail = RetreatData

type FetchResult = RetreatDetail | { notPublished: true; kind?: 'retreat' | 'event' }

interface Props {
  initData: InitData
  retreatId?: string
  /** `hide-image` attribute: skip the hero photo, keep the title/dates banner. */
  hideImage?: boolean
}

/**
 * RetreatWidget (embed detail) — full single-retreat page for the tenant's own
 * WordPress site. Renders hero, description, gallery, facilitators, package
 * tiers with live availability, location, FAQ and the legally required
 * package-travel disclosure. Booking runs inline in RetreatBookingModal (who /
 * participants / questions / payment choice); only the payment step redirects,
 * to Stripe, and the return URL targets the tenant's own site. A linked CMS
 * landing page is still REQUIRED for booking: it is where Stripe sends the
 * customer back (the /retreat-confirmation fallback is not built), so the
 * Book buttons only render when `landingPath` is set. Requires a `retreat-id`
 * attribute.
 */
export function RetreatWidget({ initData, retreatId, hideImage = false }: Props) {
  const str = t(initData.locale)
  const dateLocale = intlLocale(initData.locale)
  const [data, setData] = useState<RetreatDetail | null>(null)
  const [notPublished, setNotPublished] = useState(false)
  // API'et sender kind med for et ikke-udgivet event, så teksten ikke siger «retreat».
  const [notPublishedKind, setNotPublishedKind] = useState<'retreat' | 'event'>('retreat')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // The tier whose booking modal is open (null = closed). Booking runs inline
  // in a modal on the studio's own site; only payment redirects.
  const [bookingTier, setBookingTier] = useState<RetreatTier | null>(null)

  const fetchRetreat = useCallback(async () => {
    if (!retreatId) {
      setError('A retreat-id attribute is required.')
      setLoading(false)
      return
    }
    const result = await apiClient.get<FetchResult>(`/api/widget/retreats/${retreatId}`)
    if (result.error) {
      setError(result.error)
    } else if (result.data) {
      if ('notPublished' in result.data) {
        setNotPublished(true)
        setNotPublishedKind(result.data.kind === 'event' ? 'event' : 'retreat')
      } else {
        setData(result.data)
      }
    }
    setLoading(false)
  }, [retreatId])

  useEffect(() => {
    fetchRetreat()
  }, [fetchRetreat])

  if (loading) {
    return (
      <div>
        <style>{styles}</style>
        <div class="hsr-skel hsr-skel-hero" />
        <div class="hsr-wrap">
          <div class="hsr-skel hsr-skel-line" style={{ width: '60%', height: '28px', margin: '1.5rem 0' }} />
          <div class="hsr-skel hsr-skel-line" />
          <div class="hsr-skel hsr-skel-line" style={{ width: '90%' }} />
          <div class="hsr-skel hsr-skel-line" style={{ width: '75%' }} />
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div>
        <style>{styles}</style>
        <div role="alert" style={{ padding: '1rem 1.5rem', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 'var(--yoga-radius, 0)', color: '#b91c1c', maxWidth: '600px', margin: '0 auto' }}>
          <strong>⚠️ {error}</strong>
        </div>
      </div>
    )
  }

  if (notPublished || !data) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center', color: '#737373' }}>
        <div style={{ fontSize: '2.5rem', marginBottom: '1rem' }}>🌿</div>
        <p>{notPublishedKind === 'event' ? str.eventNotPublished : str.retreatNotPublished}</p>
      </div>
    )
  }

  const siteUrl = tenantSiteUrl(initData)
  const bookHref = data.landingPath ? `${siteUrl}${data.landingPath}` : null
  const showHeroImage = !!data.heroImageUrl && !hideImage
  // Efter tilmeldingsfristen: ingen «Book»-knapper, men «Tilmelding lukket».
  const registrationClosed = isRegistrationClosed(data)
  const isEvent = data.kind === 'event'

  return (
    <div>
      <style>{styles}</style>

      {/* Hero — `hide-image` keeps the title/dates banner but drops the photo */}
      <div class="hsr-hero">
        {showHeroImage && (
          <img src={assetUrl(data.heroImageUrl!)} alt={data.title} class="hsr-hero-img" />
        )}
        <div class={`hsr-hero-overlay${showHeroImage ? '' : ' hsr-hero-plain'}`}>
          <div class="hsr-hero-dates">{fmtDatoInterval(data.startsAt, data.endsAt, dateLocale, studietsTidszone(initData.timezone))}</div>
          <h2 class="hsr-title">{data.title}</h2>
          {data.locationName && <div class="hsr-hero-location">📍 {data.locationName}</div>}
        </div>
      </div>

      <div class="hsr-wrap">
        {/* Description */}
        {(data.shortDescription || data.description) && (
          <section class="hsr-section">
            <h3 class="hsr-h3">{isEvent ? str.eventAbout : str.retreatAbout}</h3>
            {data.shortDescription && <p class="hsr-lead">{data.shortDescription}</p>}
            {/* Same minimal markdown as the landing page's Description.tsx:
                escaped input, bold, italic, lists, headings and autolinks. */}
            {data.description && parseMarkdown(data.description).map((b, i) => {
              if (b.kind === 'h2') return <h4 key={i} class="hsr-md-h2" dangerouslySetInnerHTML={{ __html: b.html }} />
              if (b.kind === 'h3') return <h5 key={i} class="hsr-md-h3" dangerouslySetInnerHTML={{ __html: b.html }} />
              if (b.kind === 'ul') return <ul key={i} class="hsr-md-list" dangerouslySetInnerHTML={{ __html: b.html }} />
              if (b.kind === 'ol') return <ol key={i} class="hsr-md-list hsr-md-ol" dangerouslySetInnerHTML={{ __html: b.html }} />
              return <p key={i} class="hsr-para" dangerouslySetInnerHTML={{ __html: b.html }} />
            })}
          </section>
        )}

        {/* Gallery */}
        {data.galleryImageUrls.length > 0 && (
          <section class="hsr-section">
            <div class="hsr-gallery">
              {data.galleryImageUrls.map((url, i) => (
                <img key={i} src={assetUrl(url)} alt="" class="hsr-gallery-img" loading="lazy" />
              ))}
            </div>
          </section>
        )}

        {/* Tiers */}
        {data.tiers.length > 0 && (
          <section class="hsr-section">
            <h3 class="hsr-h3">{isEvent ? str.eventChooseTier : str.retreatChooseTier}</h3>
            {registrationClosed && <p class="hsr-closed" role="status">{str.registrationClosed}</p>}
            <div class="hsr-tiers">
              {data.tiers.map(tier => {
                const soldOut = tier.capacityStatus === 'sold_out'
                return (
                  <div key={tier.id} class={`hsr-tier${soldOut ? ' hsr-tier-sold' : ''}`}>
                    <div class="hsr-tier-head">
                      <span class="hsr-tier-name">{tier.name}</span>
                      <span class="hsr-tier-price">{formatMoney(tier.price, initData.locale, data.currency)}</span>
                    </div>
                    {tier.description && <p class="hsr-tier-desc">{tier.description}</p>}
                    {tier.amenities.length > 0 && (
                      <ul class="hsr-tier-amenities">
                        {tier.amenities.map((a, i) => <li key={i}>{a}</li>)}
                      </ul>
                    )}
                    <div class="hsr-tier-foot">
                      {soldOut ? (
                        <span class="hsr-tier-spots hsr-full">{str.soldOut}</span>
                      ) : tier.capacityStatus === 'few_left' ? (
                        <span class="hsr-tier-spots hsr-few">{str.fewSpotsLeft}</span>
                      ) : (
                        <span class="hsr-tier-spots hsr-open">
                          {tier.availableSpots} {tier.availableSpots === 1 ? str.spotAvailable : str.spotsAvailable}
                        </span>
                      )}
                      {/* Gated on a linked landing page: the modal books in place, but
                          Stripe sends the customer back to that page for the
                          confirmation state. Without one the server falls back to
                          /retreat-confirmation, which does not exist yet (404 after
                          payment) — so keep the button hidden until it does. */}
                      {!soldOut && bookHref && !registrationClosed && (
                        <button type="button" class="hsr-tier-cta" onClick={() => setBookingTier(tier)}>{str.book}</button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </section>
        )}

        {/* Instructors */}
        {data.instructors.length > 0 && (
          <section class="hsr-section">
            <h3 class="hsr-h3">{str.retreatInstructors}</h3>
            <div class="hsr-instructors">
              {data.instructors.map(inst => (
                <div key={inst.id} class="hsr-instructor">
                  {inst.profileImageUrl ? (
                    <img src={assetUrl(inst.profileImageUrl)} alt={`${inst.firstName} ${inst.lastName}`} class="hsr-instructor-img" loading="lazy" />
                  ) : (
                    <div class="hsr-instructor-img hsr-instructor-img-fallback">
                      <span aria-hidden="true">{(inst.firstName.charAt(0) || '?').toUpperCase()}</span>
                    </div>
                  )}
                  <div class="hsr-instructor-body">
                    <div class="hsr-instructor-name">{inst.firstName} {inst.lastName}</div>
                    {inst.bio && <p class="hsr-instructor-bio">{inst.bio}</p>}
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        {/* Location */}
        {(data.locationName || data.locationAddress) && (
          <section class="hsr-section">
            <h3 class="hsr-h3">{str.retreatLocation}</h3>
            {data.locationName && <div class="hsr-loc-name">{data.locationName}</div>}
            {data.locationAddress && <div class="hsr-loc-addr">{data.locationAddress}</div>}
            {data.locationMapUrl && (
              <a href={data.locationMapUrl} target="_blank" rel="noopener noreferrer" class="hsr-map-link">
                {str.showOnMap}
              </a>
            )}
          </section>
        )}

        {/* FAQ */}
        {data.faqItems.length > 0 && (
          <section class="hsr-section">
            <h3 class="hsr-h3">{str.retreatFaq}</h3>
            <div class="hsr-faq">
              {data.faqItems.map((item, i) => (
                <details key={i} class="hsr-faq-item">
                  <summary class="hsr-faq-q">{item.q}</summary>
                  <p class="hsr-faq-a">{item.a}</p>
                </details>
              ))}
            </div>
          </section>
        )}

        {/* Main book CTA */}
        {bookHref && data.tiers.length === 0 && (
          registrationClosed
            ? <p class="hsr-closed" role="status">{str.registrationClosed}</p>
            : <a href={bookHref} {...udLink(bookHref)} class="hsr-book-cta">{str.book}</a>
        )}

        {/* Package-travel disclosure (Rejsegarantifonden) */}
        {data.packageTravel && data.packageTravel.rgfMemberNumber && (
          <div class="hsr-package">
            <div class="hsr-package-label">{str.retreatOrganizer}: {data.packageTravel.organizerName}</div>
            <p class="hsr-package-text">
              {str.retreatPackageInfo
                .replace(/\{organizer\}/g, data.packageTravel.organizerName)
                .replace('{regNo}', data.packageTravel.rgfMemberNumber)}
            </p>
            {data.packageTravel.rgfLogoUrl && (
              <img src={assetUrl(data.packageTravel.rgfLogoUrl)} alt="" class="hsr-package-logo" loading="lazy" />
            )}
          </div>
        )}
      </div>

      {bookingTier && !registrationClosed && (
        <RetreatBookingModal
          retreat={data}
          tier={bookingTier}
          locale={initData.locale || 'da'}
          initData={initData}
          onClose={() => setBookingTier(null)}
        />
      )}
    </div>
  )
}

const styles = `
  .hsr-wrap { max-width: 860px; margin: 0 auto; }
  .hsr-hero {
    position: relative;
    max-width: 1100px;
    margin: 0 auto 0.5rem;
    border-radius: var(--yoga-radius, 0);
    overflow: hidden;
  }
  .hsr-hero-img { display: block; width: 100%; height: 380px; object-fit: cover; }
  .hsr-hero-overlay {
    position: absolute;
    left: 0; right: 0; bottom: 0;
    padding: 2.5rem 2rem 1.75rem;
    background: linear-gradient(to top, rgba(0,0,0,0.72), rgba(0,0,0,0));
    color: #fff;
  }
  .hsr-hero-overlay.hsr-hero-plain {
    position: relative;
    background: linear-gradient(135deg, var(--yoga-primary, #FF6B35), var(--yoga-secondary, var(--yoga-primary-900, #1A1A2E)));
    padding: 3rem 2rem;
  }
  .hsr-hero-dates {
    font-size: 0.78rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    margin-bottom: 0.5rem;
    opacity: 0.95;
  }
  .hsr-title {
    font-size: 2.1rem;
    font-weight: 800;
    margin: 0;
    letter-spacing: -0.02em;
    line-height: 1.15;
  }
  .hsr-hero-location { margin-top: 0.5rem; font-size: 0.95rem; opacity: 0.95; }
  .hsr-section { margin: 2.25rem 0; }
  .hsr-h3 {
    font-size: 1.25rem;
    font-weight: 700;
    margin: 0 0 1rem;
    color: var(--yoga-text, #0a0a0a);
    letter-spacing: -0.01em;
  }
  .hsr-lead {
    font-size: 1.05rem;
    line-height: 1.6;
    color: var(--yoga-text, #0a0a0a);
    margin: 0 0 1rem;
    font-weight: 500;
  }
  .hsr-para {
    font-size: 0.95rem;
    line-height: 1.65;
    color: var(--yoga-text-muted, #525252);
    margin: 0 0 0.85rem;
    white-space: pre-line;
  }
  /* Markdown blocks in the description (see ./markdown.ts) */
  .hsr-para strong { color: var(--yoga-text, #0a0a0a); font-weight: 700; }
  .hsr-md-h2, .hsr-md-h3 { color: var(--yoga-text, #0a0a0a); margin: 1.5rem 0 0.5rem; line-height: 1.3; }
  .hsr-md-h2 { font-size: 1.15rem; font-weight: 700; }
  .hsr-md-h3 { font-size: 1.02rem; font-weight: 600; }
  .hsr-md-list {
    font-size: 0.95rem;
    line-height: 1.65;
    color: var(--yoga-text-muted, #525252);
    margin: 0 0 0.85rem;
    padding-left: 1.5rem;
    list-style: disc;
  }
  .hsr-md-ol { list-style: decimal; }
  .hsr-md-list li { margin: 0.2rem 0; }
  .hsr-md-link { color: var(--yoga-primary, #FF6B35); text-decoration: underline; }
  .hsr-gallery {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 0.75rem;
  }
  .hsr-gallery-img {
    width: 100%;
    height: 160px;
    object-fit: cover;
    border-radius: var(--yoga-radius, 0);
  }
  .hsr-tiers { display: flex; flex-direction: column; gap: 1rem; }
  .hsr-tier {
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 1.25rem 1.5rem;
    background: var(--yoga-card-bg, #fff);
  }
  .hsr-tier-sold { opacity: 0.7; }
  .hsr-tier-head {
    display: flex;
    justify-content: space-between;
    align-items: baseline;
    gap: 1rem;
    flex-wrap: wrap;
  }
  .hsr-tier-name { font-size: 1.1rem; font-weight: 700; color: var(--yoga-text, #0a0a0a); }
  .hsr-tier-price { font-size: 1.35rem; font-weight: 800; color: var(--yoga-text, #0a0a0a); letter-spacing: -0.02em; }
  .hsr-tier-desc { font-size: 0.9rem; color: var(--yoga-text-muted, #525252); line-height: 1.55; margin: 0.6rem 0 0; }
  .hsr-tier-amenities {
    list-style: none;
    padding: 0;
    margin: 0.85rem 0 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.4rem 1rem;
  }
  .hsr-tier-amenities li {
    font-size: 0.82rem;
    color: var(--yoga-text-muted, #525252);
    position: relative;
    padding-left: 1.1rem;
  }
  .hsr-tier-amenities li::before {
    content: '✓';
    position: absolute;
    left: 0;
    color: #15803d;
    font-weight: 700;
  }
  .hsr-tier-foot {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 1rem;
    margin-top: 1rem;
    padding-top: 0.85rem;
    border-top: 1px solid var(--yoga-card-border, #e5e7eb);
    flex-wrap: wrap;
  }
  .hsr-tier-spots { font-size: 0.82rem; font-weight: 600; }
  .hsr-tier-spots.hsr-open { color: #15803d; }
  .hsr-tier-spots.hsr-few { color: #b45309; }
  .hsr-tier-spots.hsr-full { color: #b91c1c; }
  .hsr-tier-cta, .hsr-book-cta {
    display: inline-block;
    padding: 0.7rem 1.5rem;
    background: var(--yoga-primary, #FF6B35);
    color: #fff;
    border: 0;
    cursor: pointer;
    font-family: inherit;
    font-weight: 600;
    border-radius: var(--yoga-radius, 0);
    text-decoration: none;
    font-size: 0.9rem;
    transition: filter .2s;
  }
  .hsr-tier-cta:hover, .hsr-book-cta:hover { filter: brightness(0.94); }
  .hsr-closed { margin: 0 0 1rem; padding: 0.75rem 1rem; background: #f5f5f5; border: 1px solid #e5e5e5; border-radius: var(--yoga-radius, 0); color: #404040; font-weight: 600; text-align: center; }
  .hsr-book-cta { display: block; text-align: center; margin: 1.5rem 0; padding: 1rem; }
  .hsr-instructors { display: flex; flex-direction: column; gap: 1.25rem; }
  .hsr-instructor { display: flex; gap: 1rem; align-items: flex-start; }
  .hsr-instructor-img {
    width: 72px;
    height: 72px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }
  .hsr-instructor-img-fallback {
    background: linear-gradient(135deg, var(--yoga-primary, #FF6B35), var(--yoga-secondary, var(--yoga-primary-900, #1A1A2E)));
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.6rem;
    font-weight: 800;
  }
  .hsr-instructor-name { font-weight: 700; color: var(--yoga-text, #0a0a0a); margin-bottom: 0.25rem; }
  .hsr-instructor-bio { font-size: 0.9rem; color: var(--yoga-text-muted, #525252); line-height: 1.55; margin: 0; }
  .hsr-loc-name { font-weight: 600; color: var(--yoga-text, #0a0a0a); }
  .hsr-loc-addr { color: var(--yoga-text-muted, #525252); margin-top: 0.25rem; }
  .hsr-map-link {
    display: inline-block;
    margin-top: 0.6rem;
    color: var(--yoga-primary, #FF6B35);
    font-weight: 600;
    font-size: 0.9rem;
    text-decoration: none;
  }
  .hsr-map-link:hover { text-decoration: underline; }
  .hsr-faq { display: flex; flex-direction: column; gap: 0.5rem; }
  .hsr-faq-item {
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
    padding: 0.85rem 1.1rem;
  }
  .hsr-faq-q { font-weight: 600; color: var(--yoga-text, #0a0a0a); cursor: pointer; font-size: 0.95rem; }
  .hsr-faq-a { font-size: 0.9rem; color: var(--yoga-text-muted, #525252); line-height: 1.6; margin: 0.75rem 0 0; }
  .hsr-package {
    margin: 2.5rem 0 0;
    padding: 1.25rem 1.5rem;
    background: rgba(0,0,0,0.03);
    border: 1px solid var(--yoga-card-border, #e5e7eb);
    border-radius: var(--yoga-radius, 0);
  }
  .hsr-package-label {
    font-size: 0.8rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--yoga-text-muted, #737373);
    margin-bottom: 0.5rem;
  }
  .hsr-package-text { font-size: 0.85rem; color: var(--yoga-text-muted, #525252); line-height: 1.6; margin: 0; }
  .hsr-package-logo { max-height: 60px; margin-top: 0.85rem; }
  /* Skeletons */
  .hsr-skel {
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: hsr-shimmer 1.5s infinite;
    border-radius: 4px;
  }
  .hsr-skel-hero { width: 100%; height: 380px; max-width: 1100px; margin: 0 auto; }
  .hsr-skel-line { height: 14px; width: 100%; margin-bottom: 0.6rem; }
  @keyframes hsr-shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }
  @media (max-width: 600px) {
    .hsr-hero-img, .hsr-skel-hero { height: 240px; }
    .hsr-title { font-size: 1.6rem; }
    .hsr-hero-overlay { padding: 2rem 1.25rem 1.25rem; }
  }
`
